/**
 * AgentChatPanel - Slide-in chat panel for multi-turn conversations with the AI agent
 *
 * Features:
 * - Organic constellation particle animation (pure flowing particles, no shapes)
 * - Continuous fluid motion with subtle connections
 * - Transcend blue/white color scheme throughout
 * - Real-time tool usage and reasoning display
 * - Markdown rendering for responses
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { FiSend, FiAlertCircle, FiDatabase, FiSearch, FiCpu, FiZap, FiMaximize2, FiMinimize2, FiChevronDown, FiChevronRight, FiDownload, FiCopy, FiGlobe, FiMessageCircle, FiX, FiClock, FiExternalLink, FiUsers, FiBookOpen } from 'react-icons/fi';
import { api } from '../api';
import { AGENT_CONFIG } from '../context/AgentChatContext';
import './AgentChatPanel.css';

/**
 * Parse entity links in the format [[entity_type:PITCHBOOK_ID:Name]]
 * and convert them to markdown links that route to the entity detail page.
 *
 * Examples:
 *   [[company:12345:Unity Technologies]] → [Unity Technologies](/company/12345)
 *   [[investor:67890:Andreessen Horowitz]] → [Andreessen Horowitz](/investor/67890)
 *   [[deal:11111:Unity Series D]] → [Unity Series D](/deal/11111)
 */
function parseEntityLinks(content) {
  if (!content) return content;

  // Match [[type:id:name]] pattern
  const entityLinkRegex = /\[\[(company|investor|deal):([^:\]]+):([^\]]+)\]\]/g;

  return content.replace(entityLinkRegex, (match, entityType, pitchbookId, name) => {
    // Build the route path based on entity type
    const path = `/${entityType}/${encodeURIComponent(pitchbookId)}`;
    // Return markdown link format
    return `[${name}](${path})`;
  });
}

/**
 * ChatTable - Interactive table rendered from agent's transcend-table format
 * Supports CSV and JSON export
 *
 * @param {object} data - Table data with title, columns, and rows
 * @param {function} onEntityClick - Callback for when an entity link is clicked (path) => void
 */
function ChatTable({ data, onEntityClick }) {
  const [copiedFormat, setCopiedFormat] = useState(null);

  if (!data || !data.columns || !data.rows) {
    return null;
  }

  const { title, columns, rows } = data;

  /**
   * Strip entity link syntax [[type:id:name]] -> name
   * Used for clean exports
   */
  const stripEntityLinks = (cell) => {
    if (cell == null) return '';
    return String(cell).replace(/\[\[(company|investor|deal):[^:\]]+:([^\]]+)\]\]/g, '$2');
  };

  /**
   * Render a table cell, parsing entity links if present
   */
  const renderCell = (cell) => {
    if (cell == null) return '-';
    const cellStr = String(cell);

    // Check for entity links [[type:id:name]]
    const entityLinkRegex = /\[\[(company|investor|deal):([^:\]]+):([^\]]+)\]\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = entityLinkRegex.exec(cellStr)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(cellStr.slice(lastIndex, match.index));
      }

      const [, entityType, pitchbookId, name] = match;
      const path = `/${entityType}/${encodeURIComponent(pitchbookId)}`;

      parts.push(
        <span
          key={match.index}
          className="entity-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEntityClick?.(path);
          }}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEntityClick?.(path);
            }
          }}
        >
          {name}
        </span>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < cellStr.length) {
      parts.push(cellStr.slice(lastIndex));
    }

    // If no links found, return the plain string
    return parts.length > 0 ? parts : cellStr;
  };

  // Export as CSV
  const exportCSV = () => {
    const csvContent = [
      columns.join(','),
      ...rows.map(row => row.map(cell => {
        // Strip entity links and escape commas/quotes
        const cellStr = stripEntityLinks(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title?.replace(/\s+/g, '_') || 'table'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export as JSON
  const exportJSON = () => {
    const jsonData = rows.map(row => {
      const obj = {};
      columns.forEach((col, idx) => {
        obj[col] = stripEntityLinks(row[idx]);
      });
      return obj;
    });

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title?.replace(/\s+/g, '_') || 'table'}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Copy to clipboard
  const copyToClipboard = async (format) => {
    let content;
    if (format === 'csv') {
      content = [columns.join('\t'), ...rows.map(row => row.map(cell => stripEntityLinks(cell)).join('\t'))].join('\n');
    } else {
      const jsonData = rows.map(row => {
        const obj = {};
        columns.forEach((col, idx) => {
          obj[col] = stripEntityLinks(row[idx]);
        });
        return obj;
      });
      content = JSON.stringify(jsonData, null, 2);
    }

    try {
      await navigator.clipboard.writeText(content);
      setCopiedFormat(format);
      setTimeout(() => setCopiedFormat(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="chat-table-container">
      {title && <div className="chat-table-title">{title}</div>}
      <div className="chat-table-wrapper">
        <table className="chat-table">
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx}>{renderCell(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="chat-table-actions">
        <span className="table-row-count">{rows.length} row{rows.length !== 1 ? 's' : ''}</span>
        <div className="table-export-buttons">
          <button onClick={() => copyToClipboard('csv')} className="table-action-btn" title="Copy as TSV">
            <FiCopy />
            {copiedFormat === 'csv' ? 'Copied!' : 'Copy'}
          </button>
          <button onClick={exportCSV} className="table-action-btn" title="Download CSV">
            <FiDownload />
            CSV
          </button>
          <button onClick={exportJSON} className="table-action-btn" title="Download JSON">
            <FiDownload />
            JSON
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * WebSearchSources - Display sources from Google Search grounding
 */
function WebSearchSources({ sources }) {
  if (!sources || sources.length === 0) return null;

  return (
    <div className="web-search-sources">
      <div className="sources-header">
        <FiGlobe className="sources-icon" />
        <span>Web Sources</span>
      </div>
      <div className="sources-list">
        {sources.map((source, idx) => (
          <a key={idx} href={source.url} target="_blank" rel="noopener noreferrer" className="source-link">
            {source.title || source.url}
          </a>
        ))}
      </div>
    </div>
  );
}

/**
 * Parse message content to extract tables and regular text
 * Returns array of { type: 'text' | 'table', content: string | tableData }
 */
function parseMessageContent(content) {
  if (!content) return [{ type: 'text', content: '' }];

  const parts = [];
  const tableRegex = /```transcend-table\s*\n?([\s\S]*?)\n?```/g;
  let lastIndex = 0;
  let match;

  while ((match = tableRegex.exec(content)) !== null) {
    // Add text before the table
    if (match.index > lastIndex) {
      const text = content.slice(lastIndex, match.index).trim();
      if (text) {
        parts.push({ type: 'text', content: text });
      }
    }

    // Parse and add the table
    try {
      const tableJson = match[1].trim();
      const tableData = JSON.parse(tableJson);
      parts.push({ type: 'table', content: tableData });
    } catch (e) {
      console.error('Failed to parse table JSON:', e);
      // If parsing fails, treat it as text
      parts.push({ type: 'text', content: match[0] });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    const text = content.slice(lastIndex).trim();
    if (text) {
      parts.push({ type: 'text', content: text });
    }
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: content }];
}

/**
 * Pure constellation particle system
 * Organic flowing particles with subtle connections - no intended shapes
 * Elegant, fluid animation matching the Transcend aesthetic
 */
const PARTICLE_COUNT = 50;

/**
 * MorphingThinkingIndicator - Pure constellation particle animation
 * Organic flowing particles with subtle connections - no intended shapes
 */
function MorphingThinkingIndicator({ stage, toolName }) {
  const [particles, setParticles] = useState(() =>
    Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      id: i,
      x: 30 + (Math.random() - 0.5) * 35,
      y: 30 + (Math.random() - 0.5) * 35,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      size: 0.5 + Math.random() * 0.7,
      opacity: 0.5 + Math.random() * 0.4,
      phase: Math.random() * Math.PI * 2
    }))
  );
  const animationRef = useRef(null);

  // Continuous organic particle animation
  useEffect(() => {
    let lastTime = performance.now();

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      setParticles(prev => prev.map((particle) => {
        const center = 30;
        const maxDist = 22;

        // Distance from center
        const distFromCenter = Math.sqrt(
          Math.pow(particle.x - center, 2) + Math.pow(particle.y - center, 2)
        );

        // Soft boundary - particles drift back when too far
        let ax = 0, ay = 0;
        if (distFromCenter > maxDist) {
          const pullStrength = (distFromCenter - maxDist) * 0.025;
          ax = -(particle.x - center) / distFromCenter * pullStrength;
          ay = -(particle.y - center) / distFromCenter * pullStrength;
        }

        // Gentle wandering motion
        const wanderX = Math.sin(currentTime * 0.001 + particle.phase) * 0.02;
        const wanderY = Math.cos(currentTime * 0.0008 + particle.phase * 1.3) * 0.02;

        // Update velocity with damping
        let newVx = (particle.vx + ax + wanderX) * 0.992;
        let newVy = (particle.vy + ay + wanderY) * 0.992;

        // Clamp velocity
        const maxSpeed = 0.6;
        const speed = Math.sqrt(newVx * newVx + newVy * newVy);
        if (speed > maxSpeed) {
          newVx = (newVx / speed) * maxSpeed;
          newVy = (newVy / speed) * maxSpeed;
        }

        // Update position
        let newX = particle.x + newVx;
        let newY = particle.y + newVy;

        // Size pulsing
        const sizePulse = 1 + Math.sin(currentTime * 0.002 + particle.phase) * 0.12;

        // Opacity breathing
        const opacityPulse = 0.55 + Math.sin(currentTime * 0.0018 + particle.phase * 0.7) * 0.25;

        return {
          ...particle,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy,
          currentSize: particle.size * sizePulse,
          currentOpacity: particle.opacity * opacityPulse
        };
      }));

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Generate constellation connections between nearby particles
  const connections = [];
  const connectionDistance = 10;
  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const dist = Math.hypot(
        particles[j].x - particles[i].x,
        particles[j].y - particles[i].y
      );
      if (dist < connectionDistance) {
        const opacity = (1 - dist / connectionDistance) * 0.2;
        connections.push({
          key: `${i}-${j}`,
          x1: particles[i].x,
          y1: particles[i].y,
          x2: particles[j].x,
          y2: particles[j].y,
          opacity
        });
      }
    }
  }

  return (
    <div className="thinking-indicator morphing">
      <div className="thinking-ball-container">
        <svg viewBox="0 0 60 60" className="thinking-ball-svg constellation">
          <defs>
            <linearGradient id="particleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2a5298" />
              <stop offset="100%" stopColor="#4a72b8" />
            </linearGradient>
            <filter id="particleGlow">
              <feGaussianBlur stdDeviation="0.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#2a5298" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#2a5298" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Subtle center glow */}
          <circle cx="30" cy="30" r="22" fill="url(#centerGlow)" />

          {/* Constellation connection lines */}
          <g className="particle-connections">
            {connections.map((conn) => (
              <line
                key={conn.key}
                x1={conn.x1}
                y1={conn.y1}
                x2={conn.x2}
                y2={conn.y2}
                stroke="#2a5298"
                strokeWidth="0.3"
                opacity={conn.opacity}
              />
            ))}
          </g>

          {/* Flowing particles */}
          <g className="particle-field">
            {particles.map((p) => (
              <circle
                key={p.id}
                cx={p.x}
                cy={p.y}
                r={p.currentSize || p.size}
                fill="url(#particleGradient)"
                opacity={p.currentOpacity || p.opacity}
                filter="url(#particleGlow)"
              />
            ))}
          </g>
        </svg>
      </div>

      <div className="thinking-status">
        {stage === 'reasoning' && (
          <span className="thinking-text">
            <FiCpu className="status-icon pulse" />
            Reasoning...
          </span>
        )}
        {stage === 'tool' && toolName && (
          <span className="thinking-text tool-active">
            <FiZap className="status-icon flash" />
            {toolName}
          </span>
        )}
        {stage === 'searching' && (
          <span className="thinking-text">
            <FiSearch className="status-icon pulse" />
            Searching database...
          </span>
        )}
        {(!stage || stage === 'thinking') && (
          <span className="thinking-text">
            <span className="dots">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

// Number of particles for avatar constellation - more particles for richer effect
const AVATAR_PARTICLE_COUNT = 40;

/**
 * AnimatedAgentAvatar - Per-agent icon with animated thinking state
 *
 * Static: uses the agent's embedded SVG icon (from AGENT_CONFIG) on a
 *         colored background that matches the agent's theme.
 * Thinking: flowing particle animation on the agent-colored background.
 */
function AnimatedAgentAvatar({ id, isThinking = false, agentName = 'research' }) {
  const [particles, setParticles] = useState(() =>
    Array.from({ length: AVATAR_PARTICLE_COUNT }, (_, i) => ({
      id: i,
      x: 30 + (Math.random() - 0.5) * 40,
      y: 30 + (Math.random() - 0.5) * 40,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      size: 0.4 + Math.random() * 0.6,
      opacity: 0.5 + Math.random() * 0.4,
      phase: Math.random() * Math.PI * 2
    }))
  );
  const animationRef = useRef(null);

  const agentConfig = AGENT_CONFIG[agentName] || AGENT_CONFIG.research;
  const colors = agentConfig.colors;
  const iconDataUrl = agentConfig.icon;

  // Continuous organic particle animation when thinking
  useEffect(() => {
    if (!isThinking) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }

    let lastTime = performance.now();

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      setParticles(prev => prev.map((particle) => {
        const center = 30;
        const maxDist = 20;

        const distFromCenter = Math.sqrt(
          Math.pow(particle.x - center, 2) + Math.pow(particle.y - center, 2)
        );

        let ax = 0, ay = 0;
        if (distFromCenter > maxDist) {
          const pullStrength = (distFromCenter - maxDist) * 0.03;
          ax = -(particle.x - center) / distFromCenter * pullStrength;
          ay = -(particle.y - center) / distFromCenter * pullStrength;
        }

        const wanderX = Math.sin(currentTime * 0.0012 + particle.phase) * 0.025;
        const wanderY = Math.cos(currentTime * 0.0009 + particle.phase * 1.4) * 0.025;

        let newVx = (particle.vx + ax + wanderX) * 0.99;
        let newVy = (particle.vy + ay + wanderY) * 0.99;

        const maxSpeed = 0.7;
        const speed = Math.sqrt(newVx * newVx + newVy * newVy);
        if (speed > maxSpeed) {
          newVx = (newVx / speed) * maxSpeed;
          newVy = (newVy / speed) * maxSpeed;
        }

        let newX = particle.x + newVx;
        let newY = particle.y + newVy;

        const sizePulse = 1 + Math.sin(currentTime * 0.0025 + particle.phase) * 0.15;
        const opacityPulse = 0.6 + Math.sin(currentTime * 0.002 + particle.phase * 0.8) * 0.3;

        return {
          ...particle,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy,
          currentSize: particle.size * sizePulse,
          currentOpacity: particle.opacity * opacityPulse
        };
      }));

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isThinking]);

  const connections = useMemo(() => {
    const conns = [];
    const connectionDistance = 10;
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dist = Math.hypot(
          particles[j].x - particles[i].x,
          particles[j].y - particles[i].y
        );
        if (dist < connectionDistance) {
          const opacity = (1 - dist / connectionDistance) * 0.25;
          conns.push({
            key: `${i}-${j}`,
            x1: particles[i].x,
            y1: particles[i].y,
            x2: particles[j].x,
            y2: particles[j].y,
            opacity
          });
        }
      }
    }
    return conns;
  }, [particles]);

  // Static agent icon (when not thinking)
  if (!isThinking) {
    return (
      <svg viewBox="0 0 24 24" className="agent-avatar static">
        <defs>
          <linearGradient id={`avatarGrad${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.primary} />
            <stop offset="100%" stopColor={colors.secondary} />
          </linearGradient>
        </defs>
        <rect x="2" y="2" width="20" height="20" rx="6" ry="6" fill={`url(#avatarGrad${id})`} />
        {iconDataUrl ? (
          <image href={iconDataUrl} x="2" y="2" width="20" height="20" />
        ) : (
          <circle cx="12" cy="12" r="3" fill="white" fillOpacity="0.8" />
        )}
      </svg>
    );
  }

  // Animated particles (when thinking)
  return (
    <svg viewBox="0 0 60 60" className="agent-avatar thinking constellation">
      <defs>
        <linearGradient id={`avatarGradThink${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={colors.primary} />
          <stop offset="100%" stopColor={colors.secondary} />
        </linearGradient>
        <filter id={`glow${id}`}>
          <feGaussianBlur stdDeviation="0.6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id={`centerGlow${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="white" stopOpacity="0.2" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="2" y="2" width="56" height="56" rx="14" ry="14" fill={`url(#avatarGradThink${id})`} />
      <rect x="12" y="12" width="36" height="36" rx="8" ry="8" fill={`url(#centerGlow${id})`} />

      <g className="particle-connections">
        {connections.map((conn) => (
          <line
            key={conn.key}
            x1={conn.x1}
            y1={conn.y1}
            x2={conn.x2}
            y2={conn.y2}
            stroke="white"
            strokeWidth="0.4"
            opacity={conn.opacity}
          />
        ))}
      </g>

      <g className="particle-field">
        {particles.map((p) => (
          <circle
            key={p.id}
            cx={p.x}
            cy={p.y}
            r={p.currentSize || p.size}
            fill="white"
            opacity={p.currentOpacity || p.opacity}
            filter={`url(#glow${id})`}
          />
        ))}
      </g>
    </svg>
  );
}

/**
 * ThinkingStream - Shows real-time tool calls and reasoning as they happen
 */
function ThinkingStream({ steps }) {
  if (!steps || steps.length === 0) return null;

  return (
    <div className="thinking-stream">
      {steps.map((step, idx) => (
        <div key={idx} className={`thinking-step ${step.type} ${step.status || ''} ${step.error ? 'error' : ''}`}>
          {step.type === 'tool' && (
            <>
              <span className={`step-icon ${step.status === 'running' ? 'spinning' : ''}`}>
                {step.error ? <FiAlertCircle /> : getToolIcon(step.tool)}
              </span>
              <span className="step-label">{getToolLabel(step.tool)}</span>
              {step.query && <span className="step-query">"{step.query}"</span>}
              {step.status === 'running' && (
                <span className="step-running">searching...</span>
              )}
              {step.error ? (
                <span className="step-error">{step.error}</span>
              ) : (
                step.count !== undefined && step.status !== 'running' && (
                  <span className="step-count">
                    {step.tool === 'web_search' ? `${step.count} sources` : `${step.count} found`}
                  </span>
                )
              )}
            </>
          )}
          {step.type === 'reasoning' && (
            <>
              <span className="step-icon"><FiCpu /></span>
              <span className="step-text">{step.text}</span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function getToolLabel(tool) {
  const labels = {
    // Research agent tools
    'search_sql': 'SQL Search',
    'search_sql_by_name': 'Entity Lookup',
    'search_vdb_semantic': 'Semantic Search',
    'search_vdb_keyword': 'Keyword Expansion',
    'web_search': 'Web Search',
    // Portfolio agent tools
    'get_company_context': 'Company Lookup',
    'search_portfolio_companies': 'Portfolio Search',
    'search_stakeholders': 'Stakeholder Search',
    'get_stakeholder_contact': 'Contact Lookup',
    'find_similar_companies': 'Similar Companies',
    'semantic_search_portfolio': 'Portfolio Search',
    'list_all_companies': 'List Companies',
    'batch_company_context': 'Batch Lookup',
    'get_portfolio_stats': 'Portfolio Stats',
    'knowledge_search': 'Knowledge Search',
    'knowledge_search_namespace': 'Knowledge Search',
    'crm_search_companies': 'CRM Search',
    'crm_get_company': 'CRM Lookup',
    'crm_create_note': 'CRM Note',
    'search_pipeline_deals_sql': 'Pipeline Search',
    'search_pipeline_deals': 'Pipeline Search',
    'get_pipeline_deal_details': 'Deal Details',
    'get_pipeline_stats': 'Pipeline Stats',
    // Navi agent tools (communication)
    'slack_send_message': 'Slack Message',
    'slack_send_dm': 'Slack DM',
    'slack_reply_thread': 'Thread Reply',
    'slack_add_reaction': 'Reaction',
    'slack_read_channel': 'Read Channel',
    'slack_read_thread': 'Read Thread',
    'slack_get_user': 'User Lookup',
    'slack_list_channels': 'List Channels',
    'slack_format_message': 'Format Message',
    'email_send': 'Send Email',
  };
  return labels[tool] || tool;
}

function getToolIcon(tool) {
  const icons = {
    'search_sql': <FiDatabase />,
    'search_sql_by_name': <FiDatabase />,
    'search_vdb_semantic': <FiSearch />,
    'search_vdb_keyword': <FiZap />,
    'web_search': <FiGlobe />,
    'get_company_context': <FiDatabase />,
    'search_portfolio_companies': <FiSearch />,
    'list_all_companies': <FiDatabase />,
    'batch_company_context': <FiDatabase />,
    'get_portfolio_stats': <FiDatabase />,
    'search_stakeholders': <FiUsers />,
    'get_stakeholder_contact': <FiUsers />,
    'find_similar_companies': <FiSearch />,
    'semantic_search_portfolio': <FiSearch />,
    'knowledge_search': <FiBookOpen />,
    'knowledge_search_namespace': <FiBookOpen />,
    'crm_search_companies': <FiBookOpen />,
    'crm_get_company': <FiBookOpen />,
    'crm_create_note': <FiBookOpen />,
    'search_pipeline_deals_sql': <FiDatabase />,
    'search_pipeline_deals': <FiSearch />,
    'get_pipeline_deal_details': <FiDatabase />,
    'get_pipeline_stats': <FiDatabase />,
    // Navi agent tools (communication)
    'slack_send_message': <FiMessageCircle />,
    'slack_send_dm': <FiMessageCircle />,
    'slack_reply_thread': <FiMessageCircle />,
    'slack_add_reaction': <FiMessageCircle />,
    'slack_read_channel': <FiBookOpen />,
    'slack_read_thread': <FiBookOpen />,
    'slack_get_user': <FiUsers />,
    'slack_list_channels': <FiDatabase />,
    'slack_format_message': <FiMessageCircle />,
    'email_send': <FiSend />,
  };
  return icons[tool] || <FiCpu />;
}

// Available Gemini models
const GEMINI_MODELS = [
  { id: 'gemini-3.5-flash', name: 'Gemini 3.5 Flash', description: 'Fast & efficient' },
  { id: 'gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro', description: 'Most capable' }
];

/**
 * AgentChatPanel - Multi-turn conversation interface with the research agent
 */
function AgentChatPanel({ activeAgent = 'research', searchContext, pageContext, isOpen, onClose, onNewResults, onSwitchAgent }) {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [thinkingStage, setThinkingStage] = useState(null);
  const [currentTool, setCurrentTool] = useState(null);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);  // Compact bubble mode
  const [selectedModel, setSelectedModel] = useState(GEMINI_MODELS[0].id);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [pastConversations, setPastConversations] = useState([]);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const liveThinking = true;  // Always use live streaming
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const modelSelectorRef = useRef(null);
  const historySelectorRef = useRef(null);

  // Close model selector and history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelSelectorRef.current && !modelSelectorRef.current.contains(event.target)) {
        setShowModelSelector(false);
      }
      if (historySelectorRef.current && !historySelectorRef.current.contains(event.target)) {
        setShowHistoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset and re-initialize when active agent changes (user switches agents)
  const prevAgentRef = useRef(activeAgent);
  useEffect(() => {
    if (prevAgentRef.current !== activeAgent) {
      prevAgentRef.current = activeAgent;
      if (isOpen) {
        // Reset session state and start fresh with new agent
        setSessionId(null);
        setMessages([]);
        setError(null);
        // Directly initialize — can't rely on another effect since isOpen hasn't changed
        initializeChat();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAgent]);

  // Check authentication and fetch conversation history
  useEffect(() => {
    const checkAuthAndFetchHistory = async () => {
      try {
        const token = localStorage.getItem('transcend_token');
        if (!token) {
          setIsAuthenticated(false);
          return;
        }

        const authStatus = await api.getAuthStatus(token);
        if (authStatus.authenticated) {
          setIsAuthenticated(true);
          // Fetch past conversations
          try {
            const historyData = await api.getChatConversations(5);
            setPastConversations(historyData.conversations || []);
          } catch (historyErr) {
            console.log('Could not fetch conversation history:', historyErr);
          }
        }
      } catch (err) {
        console.log('Auth check failed:', err);
        setIsAuthenticated(false);
      }
    };

    if (isOpen) {
      checkAuthAndFetchHistory();
    }
  }, [isOpen]);

  // Initialize chat when panel opens (works with or without searchContext)
  useEffect(() => {
    if (isOpen && !sessionId) {
      initializeChat();
    }
  }, [isOpen]);

  // Auto-scroll to bottom on new messages only (not on every thinking update)
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Scroll once when loading starts to show thinking indicator
  useEffect(() => {
    if (loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && !initializing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, initializing]);

  // Reset thinking state when loading ends
  useEffect(() => {
    if (!loading) {
      setThinkingStage(null);
      setCurrentTool(null);
      setThinkingSteps([]);
    }
  }, [loading]);

  const initializeChat = async () => {
    setInitializing(true);
    setError(null);
    setThinkingStage('reasoning');

    try {
      const response = await api.startAgentChat(searchContext, selectedModel, pageContext, activeAgent);
      setSessionId(response.session_id);
      setMessages([{
        role: 'assistant',
        content: response.initial_message,
        timestamp: Date.now()
      }]);
    } catch (err) {
      console.error('Failed to start chat:', err);
      setError('Failed to start chat session. Please try again.');
      setMessages([{
        role: 'assistant',
        content: 'Sorry, I couldn\'t start the chat session. Please try again.',
        isError: true,
        timestamp: Date.now()
      }]);
    } finally {
      setInitializing(false);
      setThinkingStage(null);
    }
  };

  // Handle model change - requires starting a new session
  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    setShowModelSelector(false);
    // Reset session to use new model
    if (sessionId) {
      setSessionId(null);
      setMessages([]);
      setInitializing(true);
      // Will re-initialize with new model on next render
    }
  };

  // Load a past conversation
  const loadPastConversation = async (conversationId) => {
    setLoadingHistory(true);
    setShowHistoryDropdown(false);
    setError(null);

    try {
      const response = await api.continueChatConversation(conversationId);
      setSessionId(response.session_id);

      // Convert stored messages to component format
      const loadedMessages = (response.messages || []).map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now(),
        tool_calls: msg.tool_calls || [],
        new_results: msg.new_results || [],
        webSources: msg.web_sources || []
      }));

      setMessages(loadedMessages);
      setInitializing(false);
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError('Failed to load conversation. Starting new chat instead.');
      // Fall back to starting a new chat
      initializeChat();
    } finally {
      setLoadingHistory(false);
    }
  };

  // Format date for display
  const formatConversationDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Re-initialize when model changes and we had a session
  useEffect(() => {
    if (isOpen && !sessionId && initializing) {
      initializeChat();
    }
  }, [selectedModel]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading || !sessionId) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);
    setThinkingSteps([]);

    // Add user message immediately (optimistic update)
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: Date.now()
    }]);

    setLoading(true);
    setThinkingStage('reasoning');

    // Use streaming endpoint when live thinking is enabled
    if (liveThinking) {
      try {
        const streamUrl = api.getChatStreamUrl(sessionId);
        const token = localStorage.getItem('transcend_token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(streamUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ message: userMessage })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const liveSteps = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6));

                if (event.type === 'thinking') {
                  setThinkingStage('thinking');
                } else if (event.type === 'tool_start') {
                  // Add tool as "running" status
                  liveSteps.push({
                    type: 'tool',
                    tool: event.tool,
                    query: event.args?.query || event.args?.name,
                    status: 'running'
                  });
                  setThinkingSteps([...liveSteps]);
                } else if (event.type === 'tool_complete') {
                  // Update the last step with count and "done" status
                  const lastIdx = liveSteps.length - 1;
                  if (lastIdx >= 0 && liveSteps[lastIdx].tool === event.tool) {
                    liveSteps[lastIdx] = { ...liveSteps[lastIdx], count: event.count, status: 'done' };
                    setThinkingSteps([...liveSteps]);
                  }
                } else if (event.type === 'tool_error') {
                  // Update with error
                  const lastIdx = liveSteps.length - 1;
                  if (lastIdx >= 0 && liveSteps[lastIdx].tool === event.tool) {
                    liveSteps[lastIdx] = { ...liveSteps[lastIdx], error: event.error, status: 'error' };
                    setThinkingSteps([...liveSteps]);
                  }
                } else if (event.type === 'complete') {
                  // Final response received
                  const finalSteps = (event.tool_calls || []).map(tc => ({
                    type: 'tool',
                    tool: tc.tool,
                    query: tc.args?.query || tc.args?.name,
                    count: tc.result_count,
                    error: tc.error,
                    status: tc.error ? 'error' : 'done'
                  }));

                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: event.message,
                    toolCalls: event.tool_calls,
                    newResults: event.new_results,
                    webSources: event.web_sources || [],
                    thinkingSteps: finalSteps,
                    timestamp: Date.now()
                  }]);

                  if (event.new_results?.length > 0 && onNewResults) {
                    onNewResults(event.new_results);
                  }
                } else if (event.type === 'error') {
                  throw new Error(event.message);
                }
              } catch (parseErr) {
                console.warn('Failed to parse SSE event:', parseErr);
              }
            }
          }
        }
      } catch (err) {
        console.error('Streaming chat error:', err);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Sorry, ${err.message || 'Something went wrong. Please try again.'}`,
          isError: true,
          timestamp: Date.now()
        }]);
      } finally {
        setLoading(false);
        setThinkingSteps([]);
      }
    } else {
      // Non-streaming fallback
      const thinkingTimeout = setTimeout(() => {
        setThinkingStage('thinking');
      }, 800);

      try {
        const response = await api.sendChatMessage(sessionId, userMessage);

        clearTimeout(thinkingTimeout);

        // Build thinking steps from tool calls
        const steps = (response.tool_calls || []).map(tc => ({
          type: 'tool',
          tool: tc.tool,
          query: tc.args?.query || tc.args?.name,
          count: tc.result_count,
          status: 'done'
        }));

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.message,
          toolCalls: response.tool_calls,
          newResults: response.new_results,
          webSources: response.web_sources || [],
          thinkingSteps: steps,
          timestamp: Date.now()
        }]);

        if (response.new_results?.length > 0 && onNewResults) {
          onNewResults(response.new_results);
        }

      } catch (err) {
        clearTimeout(thinkingTimeout);
        console.error('Chat error:', err);
        const errorMsg = err.response?.data?.detail || 'Something went wrong. Please try again.';
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Sorry, ${errorMsg}`,
          isError: true,
          timestamp: Date.now()
        }]);
      } finally {
        setLoading(false);
      }
    }
  }, [input, loading, sessionId, onNewResults, liveThinking]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleRetry = () => {
    setSessionId(null);
    setMessages([]);
    initializeChat();
  };

  if (!isOpen) return null;

  const currentModel = GEMINI_MODELS.find(m => m.id === selectedModel) || GEMINI_MODELS[0];

  // Get last assistant message for minimized view
  const lastAssistantMessage = messages.filter(m => m.role === 'assistant' && !m.isError).slice(-1)[0];
  const truncatedMessage = lastAssistantMessage?.content
    ? lastAssistantMessage.content.length > 150
      ? lastAssistantMessage.content.substring(0, 150) + '...'
      : lastAssistantMessage.content
    : 'Ready to help with your research...';

  // Minimized Bubble Mode - compact horizontal bar at top-right
  if (isMinimized) {
    // Full message for hover overlay
    const fullMessage = lastAssistantMessage?.content || 'Ready to help...';

    return (
      <div className="agent-chat-bubble-minimized">
        {/* Avatar - hover triggers overlay */}
        <div className="bubble-avatar-section">
          <div className="bubble-avatar">
            <AnimatedAgentAvatar id="bubble" isThinking={loading} agentName={activeAgent} />
          </div>
        </div>

        {/* Message overlay - positioned relative to main container */}
        <div className="bubble-message-overlay">
          {loading ? (
            <span className="bubble-thinking">
              <FiCpu className="pulse" /> Thinking...
            </span>
          ) : (
            <span className="bubble-text">{fullMessage}</span>
          )}
        </div>

        {/* Centered input */}
        <div className="bubble-input-container">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask anything..."
            disabled={loading || initializing}
          />
          <button
            className="bubble-send"
            onClick={sendMessage}
            disabled={loading || initializing || !input.trim()}
          >
            <FiSend />
          </button>
        </div>

        {/* Action buttons */}
        <div className="bubble-actions">
          <button
            className="bubble-expand"
            onClick={() => setIsMinimized(false)}
            title="Expand chat"
          >
            <FiMaximize2 />
          </button>
          <button
            className="bubble-close"
            onClick={handleClose}
            title="Close chat"
          >
            <FiX />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`agent-chat-overlay ${expanded ? 'expanded' : ''}`}>
      <div className="agent-chat-panel">
        {/* Header — agent-specific color */}
        <div className="chat-header" style={{
          background: `linear-gradient(135deg, ${AGENT_CONFIG[activeAgent]?.colors?.primary || '#2C3E50'} 0%, ${AGENT_CONFIG[activeAgent]?.colors?.secondary || '#34495e'} 100%)`
        }}>
          <div className="chat-title">
            <div className="chat-title-logo">
              <AnimatedAgentAvatar id="header" isThinking={loading || initializing} agentName={activeAgent} />
            </div>
            <span>{AGENT_CONFIG[activeAgent]?.name || 'Research Agent'}</span>
          </div>
          {/* Agent Selector Tabs */}
          <div className="agent-selector-tabs">
            {Object.entries(AGENT_CONFIG).map(([key, config]) => (
              <button
                key={key}
                className={`agent-tab ${activeAgent === key ? 'active' : ''}`}
                onClick={() => {
                  if (key !== activeAgent && onSwitchAgent) {
                    onSwitchAgent(key);
                  }
                }}
                title={config.description}
              >
                {config.name.replace(' Agent', '')}
              </button>
            ))}
          </div>
          <div className="chat-header-actions">
            {/* Model Selector */}
            <div className="model-selector" ref={modelSelectorRef}>
              <button
                className="model-selector-trigger"
                onClick={() => setShowModelSelector(!showModelSelector)}
                title="Change AI model"
              >
                <span className="model-name">{currentModel.name.replace('Gemini 3 ', '')}</span>
                <FiChevronDown className={`chevron ${showModelSelector ? 'open' : ''}`} />
              </button>
              {showModelSelector && (
                <div className="model-selector-dropdown">
                  {GEMINI_MODELS.map(model => (
                    <button
                      key={model.id}
                      className={`model-option ${model.id === selectedModel ? 'active' : ''}`}
                      onClick={() => handleModelChange(model.id)}
                    >
                      <span className="model-option-name">{model.name}</span>
                      <span className="model-option-desc">{model.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Conversation History (only shown if authenticated) */}
            {isAuthenticated && (
              <div className="history-selector" ref={historySelectorRef}>
                <button
                  className="history-selector-trigger"
                  onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                  title="View past conversations"
                  disabled={loadingHistory}
                >
                  <FiClock />
                </button>
                {showHistoryDropdown && (
                  <div className="history-selector-dropdown">
                    <div className="history-dropdown-header">Recent Conversations</div>
                    {pastConversations.length === 0 ? (
                      <div className="history-empty">No past conversations</div>
                    ) : (
                      pastConversations.map(conv => (
                        <button
                          key={conv.id}
                          className="history-option"
                          onClick={() => loadPastConversation(conv.id)}
                        >
                          <span className="history-option-title">{conv.title || 'Untitled'}</span>
                          <span className="history-option-meta">
                            {formatConversationDate(conv.last_activity)} · {conv.message_count} msgs
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Expand/Collapse Toggle */}
            <button
              className="chat-expand"
              onClick={() => setExpanded(!expanded)}
              title={expanded ? "Collapse panel" : "Expand panel"}
            >
              {expanded ? <FiMinimize2 /> : <FiMaximize2 />}
            </button>
            {/* Minimize to Bubble Mode */}
            <button
              className="chat-minimize-bubble"
              onClick={() => setIsMinimized(true)}
              title="Minimize to bubble - chat while browsing"
            >
              <FiMessageCircle />
            </button>
            {/* Close/Collapse Button - Arrow instead of X */}
            <button className="chat-close" onClick={handleClose} title="Close chat">
              <FiChevronRight />
            </button>
          </div>
        </div>

        {/* Search Context Banner */}
        {searchContext && (
          <div className="chat-context-banner">
            <span className="context-query">"{searchContext.query}"</span>
            <span className="context-count">{searchContext.total_found} results</span>
          </div>
        )}

        {/* Messages */}
        <div className="chat-messages">
          {initializing ? (
            <div className="chat-initializing">
              <div className="init-avatar">
                <AnimatedAgentAvatar id="init" isThinking={true} agentName={activeAgent} />
              </div>
              <span className="init-text">Starting conversation...</span>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={`chat-message ${msg.role} ${msg.isError ? 'error' : ''}`}
              >
                {msg.role === 'assistant' && !msg.isError && (
                  <div className="message-avatar">
                    <AnimatedAgentAvatar id={idx} isThinking={false} agentName={activeAgent} />
                  </div>
                )}

                <div className="message-body">
                  {/* Show tool calls used for this message */}
                  {msg.thinkingSteps?.length > 0 && (
                    <ThinkingStream steps={msg.thinkingSteps} />
                  )}

                  {/* Parse and render message content (text + tables) */}
                  <div className="message-content">
                    {parseMessageContent(msg.content).map((part, partIdx) => (
                      part.type === 'table' ? (
                        <ChatTable key={partIdx} data={part.content} onEntityClick={(path) => { navigate(path); onClose?.(); }} />
                      ) : (
                        <ReactMarkdown
                          key={partIdx}
                          components={{
                            p: ({children}) => <p>{children}</p>,
                            strong: ({children}) => <strong>{children}</strong>,
                            em: ({children}) => <em>{children}</em>,
                            ul: ({children}) => <ul className="md-list">{children}</ul>,
                            ol: ({children}) => <ol className="md-list">{children}</ol>,
                            li: ({children}) => <li>{children}</li>,
                            // Handle links - internal entity links use navigate, external open in new tab
                            a: ({href, children}) => {
                              const isInternalEntity = href && (
                                href.startsWith('/company/') ||
                                href.startsWith('/investor/') ||
                                href.startsWith('/deal/')
                              );
                              if (isInternalEntity) {
                                return (
                                  <span
                                    className="entity-link"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(href);
                                      onClose?.(); // Close chat panel when navigating
                                    }}
                                    role="link"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        navigate(href);
                                        onClose?.();
                                      }
                                    }}
                                  >
                                    {children}
                                  </span>
                                );
                              }
                              // External link - open in new tab
                              return (
                                <a href={href} target="_blank" rel="noopener noreferrer" className="external-link">
                                  {children}
                                  <FiExternalLink className="external-link-icon" />
                                </a>
                              );
                            },
                            code: ({inline, children}) =>
                              inline ? <code className="md-code-inline">{children}</code> : <pre className="md-code-block"><code>{children}</code></pre>,
                            h1: ({children}) => <h3 className="md-heading">{children}</h3>,
                            h2: ({children}) => <h4 className="md-heading">{children}</h4>,
                            h3: ({children}) => <h5 className="md-heading">{children}</h5>,
                          }}
                        >
                          {parseEntityLinks(part.content)}
                        </ReactMarkdown>
                      )
                    ))}
                  </div>

                  {/* Show web search sources if present */}
                  {msg.webSources?.length > 0 && (
                    <WebSearchSources sources={msg.webSources} />
                  )}

                  {/* Show new results indicator */}
                  {msg.newResults?.length > 0 && (
                    <div className="message-new-results">
                      <FiDatabase />
                      <span>Found {msg.newResults.length} new record{msg.newResults.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}

          {/* Thinking state - animated avatar with status text */}
          {loading && (
            <div className="chat-message assistant thinking">
              <div className="message-avatar thinking-avatar">
                <AnimatedAgentAvatar id="thinking" isThinking={true} agentName={activeAgent} />
              </div>
              <div className="message-body thinking-body">
                <div className="thinking-status-inline">
                  {thinkingStage === 'reasoning' && <span className="thinking-label"><FiCpu className="status-icon" /> Reasoning...</span>}
                  {thinkingStage === 'tool' && currentTool && <span className="thinking-label tool"><FiZap className="status-icon" /> {currentTool}</span>}
                  {thinkingStage === 'searching' && <span className="thinking-label"><FiSearch className="status-icon" /> Searching...</span>}
                  {(!thinkingStage || thinkingStage === 'thinking') && <span className="thinking-label"><span className="thinking-dots"><span>.</span><span>.</span><span>.</span></span></span>}
                </div>
                {thinkingSteps.length > 0 && <ThinkingStream steps={thinkingSteps} />}
              </div>
            </div>
          )}

          {/* Error state with retry */}
          {error && !initializing && (
            <div className="chat-error">
              <FiAlertCircle />
              <span>{error}</span>
              <button onClick={handleRetry} className="retry-btn">Try Again</button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chat-input-container">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={searchContext ? "Ask about these results..." : "Ask me anything about gaming & VC..."}
            disabled={loading || initializing}
            rows={1}
          />
          <button
            className="chat-send"
            onClick={sendMessage}
            disabled={loading || initializing || !input.trim()}
            title="Send message"
            style={{
              background: `linear-gradient(135deg, ${AGENT_CONFIG[activeAgent]?.colors?.primary || '#2C3E50'} 0%, ${AGENT_CONFIG[activeAgent]?.colors?.secondary || '#34495e'} 100%)`
            }}
          >
            <FiSend />
          </button>
        </div>

        {/* Footer hint */}
        <div className="chat-footer">
          <span>Enter to send</span>
          <span className="footer-divider">|</span>
          <span>Shift+Enter for new line</span>
        </div>
      </div>
    </div>
  );
}

export default AgentChatPanel;
